/**
 * color-group-swatches.js
 * Runs once on page load, populates all .color-group-swatches containers.
 *
 * Collection card hover behaviour:
 *   - Injects a temporary overlay <img> over the card gallery on swatch hover
 *   - Updates the card's main anchor href to point to the hovered product
 *   - Reverts both on mouseleave
 *   - Clicking navigates directly to that product
 *
 * Swatch display priority:
 *   1. Featured (primary) image — 160x200 centre-crop (retina-sharp 4:5,
 *      matches the 64x80 PDP tiles)
 *   2. Custom swatch image from custom.swatch_image (if set, no featured img)
 *   3. Hex color from custom.swatch_color
 *
 * Colour name sourced from the product's Colour/Color option value.
 * Loaded globally from theme.liquid.
 */
(function () {
  'use strict';

  const STOREFRONT_TOKEN = '6a2db8e2de7c100bbce9b4dee5bfff84';
  const API_VERSION = '2024-10';

  // Del Día products (title contains "Del Día"/"Del Dia", incl. Dark/Earth)
  // use the rainbow swatch. Word-scoped so "Cada Día" products do NOT match.
  const DD_SWATCH = 'https://cdn.shopify.com/s/files/1/0599/3470/3780/files/dd-swatch-bg_ece67e84-2caa-45d8-a82e-59a0dd180b04.avif?v=1783145312';
  const DD_RE = /\bdel\s+d[ií]a\b/i;
  const shopDomain = window.Shopify?.shop || location.hostname;
  const endpoint = `https://${shopDomain}/api/${API_VERSION}/graphql.json`;
  const cache = {};

  function buildQuery(groupId) {
    return JSON.stringify({
      query: `{
        products(first: 20, query: "tag:color-group-${groupId}") {
          edges {
            node {
              id
              title
              handle
              options {
                name
                optionValues { name }
              }
              featuredImage {
                url(transform: { maxWidth: 800 })
                swatchUrl: url(transform: { maxWidth: 160, maxHeight: 200, crop: CENTER })
              }
              swatchColor: metafield(namespace: "custom", key: "swatch_color") {
                value
              }
              swatchImage: metafield(namespace: "custom", key: "swatch_image") {
                reference {
                  ... on MediaImage {
                    image { url(transform: { maxWidth: 160, maxHeight: 200, crop: CENTER }) }
                  }
                }
              }
            }
          }
        }
      }`
    });
  }

  // Get the colour option value from product options
  function getColorName(options) {
    if (!options) return null;
    const colorOption = options.find(o => /colou?r/i.test(o.name));
    return colorOption?.optionValues?.[0]?.name ?? null;
  }

  async function fetchGroup(groupId) {
    if (cache[groupId]) return cache[groupId];
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: buildQuery(groupId)
    });
    if (!res.ok) throw new Error(`API ${res.status}`);
    const { data } = await res.json();
    const products = (data?.products?.edges ?? []).map(e => e.node);
    cache[groupId] = products;
    return products;
  }

  // Find the card gallery element — the area containing the product image
  function findCard(container) {
    return container.closest('.product-card') || container.closest('product-card');
  }

  function findCardGallery(container) {
    const card = findCard(container);
    if (!card) return null;
    return card.querySelector('.card-gallery, slideshow-component, .product-media-container');
  }

  function findCardLink(container) {
    const card = findCard(container);
    if (!card) return null;
    return (
      card.closest('product-card-link') ||
      card.querySelector('a.product-card__link') ||
      card.querySelector('a[href*="/products/"]')
    );
  }

  function makeSwatch({ href, colorName, productTitle, hexColor, swatchImageUrl, productImageUrl, isCurrent, container }) {
    const a = document.createElement('a');
    a.href = href;
    a.className = 'color-group-swatch' + (isCurrent ? ' color-group-swatch--active' : '');
    a.setAttribute('role', 'listitem');

    const rawLabel = colorName || productTitle;
    // Capitalise first letter
    const label = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
    a.setAttribute('aria-label', label + (isCurrent ? ' (current)' : ''));
    a.setAttribute('title', label);

    // Primary image first; hex colour only when no image is available
    if (swatchImageUrl) {
      a.style.backgroundImage = `url('${swatchImageUrl}')`;
      a.style.backgroundSize = 'cover';
      a.style.backgroundPosition = 'center';
    } else if (hexColor) {
      a.style.setProperty('--swatch-bg', hexColor);
    } else {
      a.style.setProperty('--swatch-bg', '#ccc');
    }

    if (isCurrent) {
      a.setAttribute('aria-current', 'true');
    }

    if (!isCurrent && productImageUrl) {
      const gallery = findCardGallery(container);
      const cardLink = findCardLink(container);
      const originalHref = cardLink?.getAttribute('href') || null;

      let overlay = null;

      a.addEventListener('mouseenter', () => {
        if (cardLink) cardLink.setAttribute('href', href);
        if (gallery) {
          if (!overlay) {
            overlay = document.createElement('img');
            overlay.className = 'color-group-swatch-overlay';
            overlay.alt = label;
          }
          overlay.src = productImageUrl;
          gallery.appendChild(overlay);
        }
      });

      a.addEventListener('mouseleave', () => {
        if (cardLink && originalHref) cardLink.setAttribute('href', originalHref);
        if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
      });
    }

    return a;
  }

  function populateContainer(container, siblings) {
    // Guard handled by data-populated in init()

    const {
      productId: currentRawId,
      productHandle,
      productTitle,
      productColor,
      productSwatchImage,
    } = container.dataset;

    // Del Día products never group — each is one-of-a-kind. A Del Día product
    // shows only its own rainbow swatch, and Del Día products are removed
    // from every other product's swatch strip.
    if (DD_RE.test(productTitle || '')) {
      siblings = [];
    } else {
      siblings = siblings.filter(p => !DD_RE.test(p.title || ''));
    }

    if (!siblings.length) {
      container.appendChild(makeSwatch({
        href: `/products/${productHandle}`,
        colorName: null,
        productTitle,
        hexColor: productColor || null,
        swatchImageUrl: DD_RE.test(productTitle || '') ? DD_SWATCH : (productSwatchImage || null),
        productImageUrl: null,
        isCurrent: true,
        container
      }));
      return;
    }

    siblings.sort((a, b) => a.title.localeCompare(b.title));

    const isCard = !!container.closest('.product-card');

    // Build and append all swatches
    const allSwatches = [];
    siblings.forEach(({ id, title, handle, options, featuredImage, swatchColor, swatchImage }) => {
      const numericId = id.split('/').pop();
      const isCurrent = numericId === currentRawId;
      const hexColor = swatchColor?.value || null;
      const customSwatchImageUrl = swatchImage?.reference?.image?.url ?? null;
      // Del Día products get the rainbow swatch; otherwise primary image
      // leads with the custom swatch image as backup
      const swatchImageUrl = DD_RE.test(title)
        ? DD_SWATCH
        : (featuredImage?.swatchUrl || customSwatchImageUrl || null);
      const productImageUrl = featuredImage?.url ?? null;
      const colorName = getColorName(options);

      const swatch = makeSwatch({
        href: `/products/${handle}`,
        colorName,
        productTitle: title,
        hexColor,
        swatchImageUrl,
        productImageUrl,
        isCurrent,
        container
      });

      allSwatches.push({ swatch, isCurrent });
      container.appendChild(swatch);
    });

    // Product page — let swatches wrap freely, no overflow handling
    if (!isCard) return;

    // Collection cards — US style: all swatches in a scrollable strip with
    // chevron arrows shown only when the strip overflows
    const currentSwatch = allSwatches.find(s => s.isCurrent);
    const others = allSwatches.filter(s => !s.isCurrent);
    const ordered = currentSwatch ? [currentSwatch.swatch] : [];
    others.forEach(({ swatch }) => ordered.push(swatch));

    // Remove all from container, rebuild inside a scroller
    allSwatches.forEach(({ swatch }) => {
      if (swatch.parentNode) swatch.parentNode.removeChild(swatch);
    });

    const scroller = document.createElement('div');
    scroller.className = 'color-group-swatches__scroller';
    ordered.forEach(s => scroller.appendChild(s));

    function makeArrow(dir) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'color-group-swatches__arrow color-group-swatches__arrow--' + dir;
      btn.setAttribute('aria-label', dir === 'prev' ? 'Previous colours' : 'More colours');
      btn.innerHTML = dir === 'prev'
        ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>'
        : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>';
      return btn;
    }

    const prev = makeArrow('prev');
    const next = makeArrow('next');
    container.appendChild(prev);
    container.appendChild(scroller);
    container.appendChild(next);

    function updateArrows() {
      const overflowing = scroller.scrollWidth > scroller.clientWidth + 2;
      container.classList.toggle('has-overflow', overflowing);
      if (!overflowing) return;
      prev.disabled = scroller.scrollLeft <= 2;
      next.disabled = scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth - 2;
    }

    prev.addEventListener('click', () => scroller.scrollBy({ left: -scroller.clientWidth, behavior: 'smooth' }));
    next.addEventListener('click', () => scroller.scrollBy({ left: scroller.clientWidth, behavior: 'smooth' }));
    scroller.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows, { passive: true });
    requestAnimationFrame(updateArrows);
  }

  function init() {
    const containers = document.querySelectorAll('.color-group-swatches[data-color-group]');
    if (!containers.length) return;

    const groups = {};
    containers.forEach(container => {
      // Mark as being processed to avoid double processing
      if (container.dataset.populated) return;
      container.dataset.populated = 'true';

      const id = container.dataset.colorGroup;
      if (!groups[id]) groups[id] = [];
      groups[id].push(container);
    });

    if (!Object.keys(groups).length) return;

    Object.entries(groups).forEach(([groupId, containerList]) => {
      fetchGroup(decodeURIComponent(groupId))
        .then(siblings => {
          containerList.forEach(container => populateContainer(container, siblings));
        })
        .catch(err => console.warn('[Color Group Swatches]', groupId, err));
    });
  }

  // Watch for AJAX re-renders (sort, filter, infinite scroll)
  // Disconnect before running init to avoid observer triggering itself
  let debounceTimer;
  const observer = new MutationObserver(() => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      observer.disconnect();
      init();
      observer.observe(document.body, { childList: true, subtree: true });
    }, 200);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      observer.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    init();
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();